import type { TestPlayer, TestPlayersStatusFilter } from '../../lib/api/operatorConsole';
import { canLiftMark } from './canLiftMark';
import { TestPlayerRow } from './TestPlayerRow';

interface Props {
  players: TestPlayer[];
  loading: boolean;
  canEdit: boolean;
  status: TestPlayersStatusFilter;
  onStatusChange: (status: TestPlayersStatusFilter) => void;
  /** Raw input value; the page debounces it before querying. */
  search: string;
  onSearchChange: (search: string) => void;
  page: number;
  perPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /** Account ids ticked for the bulk action. */
  selected: string[];
  onSelectedChange: (accountIds: string[]) => void;
  onDeactivate: (player: TestPlayer) => void;
  onBulkDeactivate: () => void;
}

const STATUS_OPTIONS: { value: TestPlayersStatusFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

const COLUMNS = [
  'Player id',
  'Match',
  'Status',
  'Added by',
  'Added via',
  'Reason',
  'Added',
  '',
];

const headCell =
  'px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]';

export function TestPlayersList({
  players,
  loading,
  canEdit,
  status,
  onStatusChange,
  search,
  onSearchChange,
  page,
  perPage,
  totalCount,
  onPageChange,
  selected,
  onSelectedChange,
  onDeactivate,
  onBulkDeactivate,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  // Rows we placed cannot be lifted here, so they are not offered for the
  // bulk action either.
  const selectable = players.filter((p) => p.status === 'active' && canLiftMark(p));
  const allSelected =
    selectable.length > 0 && selectable.every((p) => selected.includes(p.account_id));

  function toggleOne(player: TestPlayer, checked: boolean) {
    onSelectedChange(
      checked
        ? [...selected, player.account_id]
        : selected.filter((id) => id !== player.account_id),
    );
  }

  function toggleAll(checked: boolean) {
    onSelectedChange(checked ? selectable.map((p) => p.account_id) : []);
  }

  return (
    <div className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)]">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--co-border)] px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            type="search"
            aria-label="Search player id"
            placeholder="Search player id…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-56 rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] px-2 py-1.5 text-[12px] text-[var(--co-text)] outline-none placeholder:text-[var(--co-muted)] focus:border-[var(--co-brand)]"
          />
          <select
            aria-label="Status filter"
            value={status}
            onChange={(e) => onStatusChange(e.target.value as TestPlayersStatusFilter)}
            className="rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] px-2 py-1.5 text-[12px] text-[var(--co-text)] outline-none focus:border-[var(--co-brand)]"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && (
            <>
              <span className="text-[11px] text-[var(--co-muted)]">
                {selected.length} selected
              </span>
              <button
                type="button"
                onClick={onBulkDeactivate}
                disabled={selected.length === 0}
                className="rounded-lg border border-[var(--co-border)] px-3 py-1 text-[12px] font-medium text-[var(--co-text)] hover:border-[var(--co-error)] hover:text-[var(--co-error)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Deactivate selected
              </button>
            </>
          )}
          <span className="text-[11px] text-[var(--co-muted)]">
            {totalCount} {totalCount === 1 ? 'player' : 'players'}
          </span>
        </div>
      </div>

      {loading ? (
        <p className="px-4 py-6 text-[13px] text-[var(--co-muted)]">Loading test players…</p>
      ) : players.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-[var(--co-muted)]">
          {search.trim()
            ? `No player id in this list contains “${search.trim()}”. Ids are stored exactly as submitted — a different id may still be excluded.`
            : 'No test players in this list. Everything counts towards your reports.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table aria-label="Test players" className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--co-border)]">
                {canEdit && (
                  <th className={headCell} scope="col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={selectable.length === 0}
                      onChange={(e) => toggleAll(e.target.checked)}
                      aria-label="Select every row you can deactivate"
                    />
                  </th>
                )}
                {COLUMNS.map((column, index) => (
                  <th key={column || `spacer-${index}`} className={headCell} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <TestPlayerRow
                  key={player.id}
                  player={player}
                  canEdit={canEdit}
                  selected={selected.includes(player.account_id)}
                  onSelectChange={toggleOne}
                  onDeactivate={onDeactivate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-[var(--co-border)] px-4 py-3">
          <span className="text-[11px] text-[var(--co-muted)]">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-[var(--co-border)] px-3 py-1 text-[12px] font-medium text-[var(--co-text)] disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="rounded-lg border border-[var(--co-border)] px-3 py-1 text-[12px] font-medium text-[var(--co-text)] disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
